import { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, Chip, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Skeleton, TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { getAllCampaigns, deleteCampaign, submitForApproval, approveCampaign, rejectCampaign, archiveCampaign, reactivateCampaign } from '../../api/incentiveApi';
import { useUser } from '../../context/userContext';
import CreateCampaignDialog from './createCampaignDialog';

const STATUS_CHIPS = {
  Draft: { bg: '#F1F5F9', color: '#64748B' },
  PendingApproval: { bg: '#FFFBEB', color: '#D97706' },
  Active: { bg: '#D1FAE5', color: '#065F46' },
  Ended: { bg: '#FEE2E2', color: '#991B1B' },
};

const TYPE_CHIPS = {
  Case: { bg: '#EFF6FF', color: '#1A8CFF' },
  Travel: { bg: '#F5F3FF', color: '#8B5CF6' },
  Item: { bg: '#FFFBEB', color: '#F59E0B' },
};

const FONT = { fontFamily: "'Poppins', sans-serif" };

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' });
};

export default function ManageCampaigns() {
  const { user } = useUser();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editCampaign, setEditCampaign] = useState(null);

  const fetchCampaigns = useCallback(async () => {
    try {
      const res = await getAllCampaigns();
      setCampaigns(res.data ?? []);
    } catch (err) {
      console.error('Error fetching campaigns:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  const handleCreate = () => {
    setEditCampaign(null);
    setDialogOpen(true);
  };

  const handleEdit = (c) => {
    setEditCampaign(c);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditCampaign(null);
  };

  const handleSaved = () => {
    handleDialogClose();
    fetchCampaigns();
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Delete campaign "${c.name}"? This cannot be undone.`)) return;
    try {
      await deleteCampaign(c.campaignId);
      fetchCampaigns();
    } catch (err) {
      alert('Failed to delete: ' + (err.response?.data?.message ?? err.message));
    }
  };

  const handleSubmit = async (c) => {
    if (!window.confirm(`Submit "${c.name}" for approval?`)) return;
    try {
      await submitForApproval(c.campaignId);
      fetchCampaigns();
    } catch (err) {
      alert('Failed to submit: ' + (err.response?.data?.message ?? err.message));
    }
  };

  const handleApprove = async (c) => {
    if (!window.confirm(`Approve campaign "${c.name}"?`)) return;
    try {
      await approveCampaign(c.campaignId);
      fetchCampaigns();
    } catch (err) {
      alert('Failed to approve: ' + (err.response?.data?.message ?? err.message));
    }
  };

  const handleReject = async (c) => {
    const remarks = window.prompt(`Reject "${c.name}"? Enter remarks:`);
    if (remarks == null) return; // cancelled
    try {
      await rejectCampaign(c.campaignId, remarks);
      fetchCampaigns();
    } catch (err) {
      alert('Failed to reject: ' + (err.response?.data?.message ?? err.message));
    }
  };

  const handleArchive = async (c) => {
    if (!window.confirm(`Archive campaign "${c.name}"? It will be marked as Ended.`)) return;
    try {
      await archiveCampaign(c.campaignId);
      fetchCampaigns();
    } catch (err) {
      alert('Failed to archive: ' + (err.response?.data?.message ?? err.message));
    }
  };

  const handleReactivate = async (c) => {
    if (!window.confirm(`Reactivate campaign "${c.name}"? It will be set back to Active.`)) return;
    try {
      await reactivateCampaign(c.campaignId);
      fetchCampaigns();
    } catch (err) {
      alert('Failed to reactivate: ' + (err.response?.data?.message ?? err.message));
    }
  };

  const isApprover = user?.roleRights?.includes('IncentiveManagement');

  const cellSx = { ...FONT, fontSize: '0.82rem', color: '#374151', padding: '10px 14px', borderBottom: '1px solid #F1F5F9' };
  const headSx = {
    ...FONT, fontWeight: 600, fontSize: '0.72rem', color: '#64748B',
    textTransform: 'uppercase', letterSpacing: '0.5px', backgroundColor: '#F8FAFC',
    borderBottom: '2px solid #E2E8F0', padding: '12px 14px',
  };

  if (loading) {
    return (
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#0F172A', ...FONT, mb: 0.5 }}>Manage Campaigns</Typography>
        <Skeleton variant="rounded" height={400} sx={{ borderRadius: '14px', mt: 2 }} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#0F172A', ...FONT, mb: 0.5 }}>
            Manage Campaigns
          </Typography>
          <Typography sx={{ fontSize: '0.85rem', color: '#64748B', ...FONT }}>
            Create, edit, and manage incentive campaigns
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
          sx={{
            ...FONT,
            fontWeight: 600,
            fontSize: '0.85rem',
            textTransform: 'none',
            borderRadius: '8px',
            background: 'linear-gradient(135deg, #1A8CFF, #0F62CC)',
            boxShadow: 'none',
            '&:hover': { background: 'linear-gradient(135deg, #0F62CC, #0A4FA3)', boxShadow: 'none' },
          }}
        >
          Create Campaign
        </Button>
      </Box>

      <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '14px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #E2E8F0' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Name', 'Type', 'Metric', 'Duration', 'Status', 'Actions'].map((h) => (
                <TableCell key={h} sx={headSx}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {campaigns.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} sx={{ ...cellSx, textAlign: 'center', py: 4, color: '#94A3B8' }}>
                  No campaigns found. Create one to get started.
                </TableCell>
              </TableRow>
            ) : (
              campaigns.map((c) => {
                const statusStyle = STATUS_CHIPS[c.status] ?? STATUS_CHIPS.Draft;
                const typeStyle = TYPE_CHIPS[c.type] ?? TYPE_CHIPS.Case;

                return (
                  <TableRow key={c.campaignId} onClick={(e) => {
                    if (e.target.tagName === 'SPAN' && e.target.style.cursor === 'pointer') return;
                    handleEdit(c);
                  }} sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#F0F7FF' } }}>
                    <TableCell sx={{ ...cellSx, fontWeight: 600, color: '#0F172A' }}>{c.name}</TableCell>
                    <TableCell sx={cellSx}>
                      <Chip label={c.type} size="small" sx={{ backgroundColor: typeStyle.bg, color: typeStyle.color, fontWeight: 600, fontSize: '0.7rem', ...FONT }} />
                    </TableCell>
                    <TableCell sx={cellSx}>{c.metric}</TableCell>
                    <TableCell sx={{ ...cellSx, fontSize: '0.78rem' }}>
                      {formatDate(c.startDate)} - {formatDate(c.endDate)}
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <Chip label={c.status} size="small" sx={{ backgroundColor: statusStyle.bg, color: statusStyle.color, fontWeight: 600, fontSize: '0.7rem', ...FONT }} />
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                        <span onClick={() => handleEdit(c)} style={{ color: '#1A8CFF', cursor: 'pointer', fontWeight: 500, fontSize: '0.82rem' }}>
                          Edit
                        </span>
                        {c.status === 'Draft' && (
                          <>
                            <span onClick={() => handleDelete(c)} style={{ color: '#EF4444', cursor: 'pointer', fontWeight: 500, fontSize: '0.82rem' }}>
                              Delete
                            </span>
                            <span onClick={() => handleSubmit(c)} style={{ color: '#F59E0B', cursor: 'pointer', fontWeight: 500, fontSize: '0.82rem' }}>
                              Submit
                            </span>
                          </>
                        )}
                        {c.status === 'PendingApproval' && isApprover && (
                          <>
                            <span onClick={() => handleApprove(c)} style={{ color: '#10B981', cursor: 'pointer', fontWeight: 500, fontSize: '0.82rem' }}>
                              Approve
                            </span>
                            <span onClick={() => handleReject(c)} style={{ color: '#EF4444', cursor: 'pointer', fontWeight: 500, fontSize: '0.82rem' }}>
                              Reject
                            </span>
                          </>
                        )}
                        {c.status === 'Active' && (
                          <span onClick={() => handleArchive(c)} style={{ color: '#64748B', cursor: 'pointer', fontWeight: 500, fontSize: '0.82rem' }}>
                            Archive
                          </span>
                        )}
                        {c.status === 'Ended' && (
                          <span onClick={() => handleReactivate(c)} style={{ color: '#10B981', cursor: 'pointer', fontWeight: 500, fontSize: '0.82rem' }}>
                            Reactivate
                          </span>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <CreateCampaignDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onSaved={handleSaved}
        campaign={editCampaign}
      />
    </Box>
  );
}
